/**
 * ThemeContext.js
 * Author: Markus.van.Kempen@gmail.com
 * Date: 2nd Nov 2025
 */

import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// Define available themes
export const themes = {
  dark: {
    name: 'Dark',
    id: 'dark',
    colors: {
      '--color-background': '#1a1a1a',
      '--color-surface': '#2d2d2d',
      '--color-surface-hover': '#3d3d3d',
      '--color-primary': '#61dafb',
      '--color-primary-dark': '#4fa8c5',
      '--color-secondary': '#ffd700',
      '--color-accent': '#ff6b6b',
      '--color-text-primary': '#ffffff',
      '--color-text-secondary': '#b0b0b0',
      '--color-border': '#404040',
      '--color-success': '#48bb78',
      '--color-warning': '#ed8936',
      '--color-error': '#f56565',
      '--color-info': '#4299e1',
      '--color-card-bg': '#2d2d2d',
      '--color-input-bg': '#1a1a1a',
      '--color-shadow': 'rgba(0, 0, 0, 0.3)',
      '--color-on-primary': '#1a1a1a',
    }
  },
  light: {
    name: 'Light',
    id: 'light',
    colors: {
      '--color-background': '#f5f5f5',
      '--color-surface': '#ffffff',
      '--color-surface-hover': '#f0f0f0',
      '--color-primary': '#0088cc',
      '--color-primary-dark': '#006699',
      '--color-secondary': '#ff9800',
      '--color-accent': '#e91e63',
      '--color-text-primary': '#1a1a1a',
      '--color-text-secondary': '#666666',
      '--color-border': '#e0e0e0',
      '--color-success': '#2e7d32',
      '--color-warning': '#f57c00',
      '--color-error': '#c62828',
      '--color-info': '#1976d2',
      '--color-card-bg': '#ffffff',
      '--color-input-bg': '#ffffff',
      '--color-shadow': 'rgba(0, 0, 0, 0.1)',
      '--color-on-primary': '#ffffff',
    }
  },
  ocean: {
    name: 'Ocean Blue',
    id: 'ocean',
    colors: {
      '--color-background': '#0a1929',
      '--color-surface': '#1e3a5f',
      '--color-surface-hover': '#2a4a70',
      '--color-primary': '#4dd0e1',
      '--color-primary-dark': '#26c6da',
      '--color-secondary': '#80deea',
      '--color-accent': '#00acc1',
      '--color-text-primary': '#e1f5fe',
      '--color-text-secondary': '#b3e5fc',
      '--color-border': '#2a4a70',
      '--color-success': '#26a69a',
      '--color-warning': '#ffa726',
      '--color-error': '#ef5350',
      '--color-info': '#42a5f5',
      '--color-card-bg': '#1e3a5f',
      '--color-input-bg': '#0a1929',
      '--color-shadow': 'rgba(0, 0, 0, 0.4)',
      '--color-on-primary': '#0a1929',
    }
  },
  forest: {
    name: 'Forest Green',
    id: 'forest',
    colors: {
      '--color-background': '#1b2617',
      '--color-surface': '#2d3b28',
      '--color-surface-hover': '#3d4b38',
      '--color-primary': '#66bb6a',
      '--color-primary-dark': '#4caf50',
      '--color-secondary': '#aed581',
      '--color-accent': '#7cb342',
      '--color-text-primary': '#e8f5e9',
      '--color-text-secondary': '#c5e1a5',
      '--color-border': '#3d4b38',
      '--color-success': '#66bb6a',
      '--color-warning': '#ffb74d',
      '--color-error': '#ef5350',
      '--color-info': '#42a5f5',
      '--color-card-bg': '#2d3b28',
      '--color-input-bg': '#1b2617',
      '--color-shadow': 'rgba(0, 0, 0, 0.4)',
      '--color-on-primary': '#1b2617',
    }
  },
  sunset: {
    name: 'Sunset',
    id: 'sunset',
    colors: {
      '--color-background': '#2c1810',
      '--color-surface': '#3d2618',
      '--color-surface-hover': '#4d3628',
      '--color-primary': '#ff7043',
      '--color-primary-dark': '#f4511e',
      '--color-secondary': '#ffab91',
      '--color-accent': '#ff5722',
      '--color-text-primary': '#fff3e0',
      '--color-text-secondary': '#ffccbc',
      '--color-border': '#4d3628',
      '--color-success': '#66bb6a',
      '--color-warning': '#ffa726',
      '--color-error': '#ef5350',
      '--color-info': '#42a5f5',
      '--color-card-bg': '#3d2618',
      '--color-input-bg': '#2c1810',
      '--color-shadow': 'rgba(0, 0, 0, 0.4)',
      '--color-on-primary': '#fff3e0',
    }
  },
  midnight: {
    name: 'Midnight Purple',
    id: 'midnight',
    colors: {
      '--color-background': '#1a0f2e',
      '--color-surface': '#2d1b4e',
      '--color-surface-hover': '#3d2b5e',
      '--color-primary': '#ab47bc',
      '--color-primary-dark': '#8e24aa',
      '--color-secondary': '#ce93d8',
      '--color-accent': '#ba68c8',
      '--color-text-primary': '#f3e5f5',
      '--color-text-secondary': '#e1bee7',
      '--color-border': '#3d2b5e',
      '--color-success': '#66bb6a',
      '--color-warning': '#ffa726',
      '--color-error': '#ef5350',
      '--color-info': '#42a5f5',
      '--color-card-bg': '#2d1b4e',
      '--color-input-bg': '#1a0f2e',
      '--color-shadow': 'rgba(0, 0, 0, 0.4)',
      '--color-on-primary': '#f3e5f5',
    }
  }
};

export const ThemeProvider = ({ children }) => {
  const [currentTheme, setCurrentTheme] = useState('dark');
  const [isLoading, setIsLoading] = useState(true);

  // Load theme from localStorage on mount
  useEffect(() => {
    const savedTheme = localStorage.getItem('appTheme');
    if (savedTheme && themes[savedTheme]) {
      setCurrentTheme(savedTheme);
    }
    setIsLoading(false);
  }, []);

  // Apply theme colors to CSS variables
  useEffect(() => {
    const theme = themes[currentTheme];
    if (theme) {
      const root = document.documentElement;
      Object.entries(theme.colors).forEach(([property, value]) => {
        root.style.setProperty(property, value);
      });
      
      // Add theme class to body for any theme-specific styles
      document.body.className = `theme-${currentTheme}`;
    }
  }, [currentTheme]);

  const changeTheme = (themeId) => {
    if (themes[themeId]) {
      setCurrentTheme(themeId);
      localStorage.setItem('appTheme', themeId);
    }
  };

  const value = {
    currentTheme,
    changeTheme,
    availableThemes: Object.values(themes),
    isLoading,
    theme: themes[currentTheme]
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

